/**
 * Section E Page - E.在地利害關係人諮詢
 * Local stakeholder consultation
 * UI 模式：5 單層頁籤（E.1～E.5）
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import RichTextEditor from '../components/RichTextEditor';
import sectionEService, {
  SectionEDetail, SectionEFieldKey,
} from '../services/sectionEService';
import FunctionPageHeader from '../components/FunctionPageHeader';

// ── SaveFeedback ──────────────────────────────────────────────────
function useSaveFeedback() {
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const show = (ok: boolean, text: string) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 2500);
  };
  return { msg, show };
}

// ── TextTab ───────────────────────────────────────────────────────
interface TextTabProps {
  projectId: number;
  fieldKey: SectionEFieldKey;
  initialValue: string | null;
  canEdit: boolean;
  onSaved: (d: SectionEDetail) => void;
}

const TextTab: React.FC<TextTabProps> = ({ projectId, fieldKey, initialValue, canEdit, onSaved }) => {
  const { t } = useTranslation();
  const { msg, show } = useSaveFeedback();
  const [value, setValue] = useState(initialValue || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(initialValue || ''); }, [initialValue]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await sectionEService.saveSectionE(projectId, { [fieldKey]: value });
      show(true, t('sectionE.saveSuccess'));
      onSaved(res);
    } catch {
      show(false, t('sectionE.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '8px 0' }}>
      <RichTextEditor value={value} onChange={setValue} disabled={!canEdit} minHeight="200px" />
      {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 24px', fontSize: 14, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#16a085', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
            {saving ? t('sectionE.saving') : t('sectionE.save')}
          </button>
          {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Main Page
// ════════════════════════════════════════════════════════════════
type ETab = 'e1' | 'e2' | 'e3' | 'e4' | 'e5';

const fieldMap: Record<ETab, SectionEFieldKey> = {
  e1: 'scope_of_consultation',
  e2: 'stakeholders_invited',
  e3: 'consultation_modalities',
  e4: 'comments_summary',
  e5: 'comments_consideration',
};

const SectionEPage: React.FC = () => {
  const { t } = useTranslation();
  const { selectedProjectId } = useProject();
  const { hasPermission } = usePermission();
  const canUpdate = hasPermission('section_e', 'update');

  const [detail, setDetail] = useState<SectionEDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<ETab>('e1');
  const [resetConfirm, setResetConfirm] = useState(false);

  // ── Load ──────────────────────────────────────────────────────
  const loadData = useCallback(async (projectId: number) => {
    setLoading(true);
    try {
      await validateSession('SectionEPage');
      const res = await sectionEService.getSectionEByProject(projectId);
      setDetail(res);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedProjectId) { setDetail(null); loadData(selectedProjectId); }
  }, [selectedProjectId, loadData]);

  // ── Reset ─────────────────────────────────────────────────────
  const handleReset = async () => {
    if (!selectedProjectId) return;
    try {
      await sectionEService.resetSectionE(selectedProjectId);
      setDetail(null);
    } catch { /* ignore */ }
    setResetConfirm(false);
  };

  // ── Tab style ─────────────────────────────────────────────────
  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '10px 22px', fontSize: 14, fontWeight: active ? 700 : 500,
    background: active ? '#16a085' : '#f5f5f5',
    color: active ? '#fff' : '#555',
    cursor: 'pointer',
    borderTop: '1px solid #e0e0e0',
    borderLeft: '1px solid #e0e0e0',
    borderRight: '1px solid #e0e0e0',
    borderBottom: active ? '2px solid #fff' : '1px solid #e0e0e0',
    borderRadius: '6px 6px 0 0',
    marginRight: 4, transition: 'all 0.15s',
  });

  const tabs: ETab[] = ['e1', 'e2', 'e3', 'e4', 'e5'];
  const tabLabelMap: Record<ETab, string> = {
    e1: t('sectionE.tabE1'),
    e2: t('sectionE.tabE2'),
    e3: t('sectionE.tabE3'),
    e4: t('sectionE.tabE4'),
    e5: t('sectionE.tabE5'),
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <FunctionPageHeader funcCode="section_e" />
        {canUpdate && detail && (
          <button onClick={() => setResetConfirm(true)}
            style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e53935', background: '#fff', color: '#e53935', cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>
            🗑 {t('sectionE.resetData')}
          </button>
        )}
      </div>

      {!selectedProjectId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>{t('sectionE.selectProject')}</div>
      ) : loading ? (
        <div className="loading">{t('sectionE.loading')}</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>

          {/* ── Tabs ── */}
          <div style={{ display: 'flex', padding: '16px 20px 0', borderBottom: '1px solid #e0e0e0', background: '#fafafa', borderRadius: '8px 8px 0 0', flexWrap: 'wrap' }}>
            {tabs.map(tab => (
              <button key={tab} style={tabStyle(activeTab === tab)} onClick={() => setActiveTab(tab)}>
                {tabLabelMap[tab]}
              </button>
            ))}
          </div>

          {/* ── Content ── */}
          <div style={{ padding: '20px 24px' }}>
            <TextTab
              key={activeTab}
              projectId={selectedProjectId!}
              fieldKey={fieldMap[activeTab]}
              initialValue={detail?.[fieldMap[activeTab]] ?? null}
              canEdit={canUpdate}
              onSaved={setDetail}
            />
          </div>
        </div>
      )}

      {/* Reset confirm */}
      {resetConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', minWidth: 360, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('sectionE.resetTitle')}</div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 20, whiteSpace: 'pre-line' }}>{t('sectionE.resetConfirm')}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setResetConfirm(false)} style={{ padding: '6px 18px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>{t('sectionE.cancel')}</button>
              <button onClick={handleReset} style={{ padding: '6px 18px', borderRadius: 6, border: 'none', background: '#ff4d4f', color: '#fff', cursor: 'pointer' }}>{t('sectionE.reset')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionEPage;
